import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import '../../../library/elements/d-menu.js';
import './d-infosec-view-header.js';
import { sortSelectedCategories } from './defaults.js';
import './editors/d-edit-computers-network.js';
import './editors/d-edit-networks-computers.js';
import './editors/d-edit-networks-external-connections.js';
import { NewNetworkDialog } from 'src/pages/computers-page/infosec-procedure/editors/new-network-dialog';
import { uuid } from 'src/utilities/text';
import { NewComputerDialog } from 'src/pages/computers-page/infosec-procedure/editors/new-computer-dialog';
import type { NewNetworkResult } from './editors/new-network-result';
import type { NewComputerResult } from './editors/new-computer-result';
import type { InfosecDataItem } from './infosecDataItem';
import type { InfosecStorageUnit } from './infosecStorageUnit';
import type { InfosecNetwork } from './infosecNetwork';
import type { InfosecAsset } from './infosecAsset';
import type { InfosecExternalConnection } from './infosecExternalConnection';
import type { SelectDropdownOption } from '../../../library/editors/elements/select-dropdown-option';
import type { Sector } from '../../../store/types';

/**
 *
 */
@customElement('d-infosec-computers-networks')
export class DInfosecComputersNetworks extends LitElement {
  static readonly styles = [css``];
  @property({ type: String })
  tab = 'networks';
  @property({ type: Array })
  dataItems: InfosecDataItem[] = [];
  @property({ type: Array })
  employees: SelectDropdownOption[] = [];
  @property({ type: Array })
  partners: SelectDropdownOption[] = [];
  @property({ type: Array })
  externalConnections: InfosecExternalConnection[] = [];

  @property({ type: Array })
  computers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  remoteServers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  cloudServices: InfosecStorageUnit[] = [];
  @property({ type: Array })
  locations: InfosecStorageUnit[] = [];
  @property({ type: Array })
  assets: InfosecAsset[] = [];
  @property({ type: Array })
  networks: InfosecNetwork[] = [];
  @property({ type: String })
  sector: Sector = 'OTHER';

  private get menuItems() {
    return [
      {
        value: 'networks',
        text: t('computers.networksTab'),
      },
      {
        value: 'computers',
        text: t('computers.localDevicesTab'),
      },
      {
        value: 'externalConnections',
        text: t('computers.externalConnectionsTab'),
      },
    ];
  }

  _networksChanged(detail) {
    this.computers = detail.computers;
    this.networks = detail.network;
  }

  render() {
    let networkTerm = t('computers.networkTermSingular');
    if (this.networks.length > 1) {
      networkTerm = t('computers.networkTermPlural');
    }
    let networksInfo = `<p>${t('computers.networksInfo')}</p>`;
    if (this.computers.length === 0) {
      networksInfo += `<p><em>${t('computers.noDevicesRegistered')}</em></p>`;
    }
    let computersInfo = `<p>${t('computers.computersInfo', { networkTerm })}</p>`;
    if (this.networks.length === 0) {
      computersInfo += `<p><em>${t('computers.noNetworksRegistered')}</em></p>`;
    }
    let externalConnectionsInfo = `<p>${t('computers.externalConnectionsInfo')}</p>`;
    if (this.networks.length === 0) {
      externalConnectionsInfo += `<p><em>${t('computers.noLocalNetworksRegistered')}</em></p>`;
    }
    return html`
      <d-menu
        small
        .items=${this.menuItems}
        .value=${this.tab}
        @value-changed=${(e) => (this.tab = e.detail.value)}
      ></d-menu>
      ${this.tab === 'networks'
        ? html`
            <d-infosec-view-header title=${t('computers.networksTab')} .info=${networksInfo}></d-infosec-view-header>
            <d-edit-computers-network
              .computers=${this.computers}
              .networks=${this.networks}
              .sector=${this.sector}
              @selected-items-changed=${(e) => this._networksChanged(e.detail)}
            ></d-edit-computers-network>
            <d-action @click=${async () => await this.addNetwork()}>${t('computers.addNetwork')}</d-action>
            <d-action @click=${async () => await this.addComputer()}>${t('computers.addDevice')}</d-action>
          `
        : nothing}
      ${this.tab === 'computers'
        ? html`
            <d-infosec-view-header
              title=${t('computers.localDevicesInNetworkTitle', { networkTerm })}
              .info=${computersInfo}
            ></d-infosec-view-header>
            <d-edit-networks-computers
              .computers=${this.computers}
              .networks=${this.networks}
            ></d-edit-networks-computers>
            <d-action @click=${async () => await this.addNetwork()}>${t('computers.addNetwork')}</d-action>
            <d-action @click=${async () => await this.addComputer()}>${t('computers.addDevice')}</d-action>
          `
        : nothing}
      ${this.tab === 'externalConnections'
        ? html`
            <d-infosec-view-header
              title=${t('computers.externalConnectionsToLocalNetworkTitle')}
              .info=${externalConnectionsInfo}
            ></d-infosec-view-header>
            <d-edit-networks-external-connections
              .networks=${this.networks}
              .computers=${this.computers}
              .employees=${this.employees}
              .partners=${this.partners}
              .externalConnections=${this.externalConnections}
            ></d-edit-networks-external-connections>
            <d-action @click=${async () => await this.addNetwork()}>${t('computers.addNetwork')}</d-action>
          `
        : nothing}
    `;
  }

  protected firstUpdated() {
    this.dataItems = sortSelectedCategories(this.dataItems);
  }

  private async addNetwork() {
    const defaultConnectionType = this.sector === 'HEALTH' ? 'NONE' : 'OTHER';
    const result: NewNetworkResult = await NewNetworkDialog.open({
      name: '',
      type: 'CABLE',
      connectionType: defaultConnectionType,
      sector: this.sector,
    });
    if (result.action === 'done') {
      const newItem: InfosecNetwork = {
        uuid: uuid(),
        name: result.name,
        type: result.type,
        connectionType: result.connectionType,
      };
      this.dispatchEvent(
        new CustomEvent<{ item: InfosecNetwork }>('new-network', {
          composed: true,
          bubbles: true,
          detail: {
            item: newItem,
          },
        }),
      );
    }
  }

  private async addComputer() {
    const result: NewComputerResult = await NewComputerDialog.open({
      name: '',
      requireNameForServers: this.computers.filter((c) => c.computerType === 'Server').length !== 0,
    });
    if (result.action === 'done') {
      const newItem = {
        uuid: uuid(),
        name: result.name,
        type: result.type,
      };
      this.dispatchEvent(
        new CustomEvent<{ uuid: string; name: string; type: string }>('new-computer', {
          composed: true,
          bubbles: true,
          detail: newItem,
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-infosec-computers-networks': DInfosecComputersNetworks;
  }
}
